import React, { useEffect, useState } from 'react';
import { SaveIcon, XIcon } from 'lucide-react';
import { Tag, TagScope, TagType } from './TagsManagement';
interface TagFormProps {
  tag: Tag | null;
  tagTypes: TagType[];
  entities: {
    id: string;
    name: string;
  }[];
  sites: {
    id: string;
    name: string;
    entityId: string;
  }[];
  vendors: {
    id: string;
    name: string;
  }[];
  onSave: (tag: Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onCancel: () => void;
  locale: string;
}
export function TagForm({
  tag,
  tagTypes,
  entities,
  sites,
  vendors,
  onSave,
  onCancel,
  locale
}: TagFormProps) {
  const [formData, setFormData] = useState<Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>>({
    name: '',
    description: '',
    tagTypeId: null,
    tagTypeName: null,
    scopeType: 'Application',
    scopeId: null,
    scopeName: null,
    colourHex: '#3B82F6',
    isActive: true,
    activeFrom: null,
    activeTo: null
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [filteredSites, setFilteredSites] = useState<{
    id: string;
    name: string;
    entityId: string;
  }[]>([]);
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [presetColors] = useState(['#EF4444', '#F97316', '#F59E0B', '#EAB308', '#84CC16', '#10B981', '#14B8A6', '#06B6D4', '#0EA5E9', '#3B82F6', '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899', '#F43F5E', '#6B7280' // Gray
  ]);
  // Initialize form data when editing a tag
  useEffect(() => {
    if (tag) {
      setFormData({
        name: tag.name,
        description: tag.description,
        tagTypeId: tag.tagTypeId,
        tagTypeName: tag.tagTypeName,
        scopeType: tag.scopeType,
        scopeId: tag.scopeId,
        scopeName: tag.scopeName,
        colourHex: tag.colourHex,
        isActive: tag.isActive,
        activeFrom: tag.activeFrom,
        activeTo: tag.activeTo
      });
      // Set the selected entity ID if the tag is scoped to a site
      if (tag.scopeType === 'Site' && tag.scopeId) {
        const site = sites.find(s => s.id === tag.scopeId);
        if (site) {
          setSelectedEntityId(site.entityId);
          setFilteredSites(sites.filter(s => s.entityId === site.entityId));
        }
      }
    }
  }, [tag, sites]);
  // Handle form field changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    if (name === 'scopeType') {
      // Reset scope-related fields when scope type changes
      setFormData({
        ...formData,
        scopeType: value as TagScope,
        scopeId: null,
        scopeName: null
      });
      setSelectedEntityId(null);
    } else if (name === 'tagTypeId') {
      // Update tag type name when tag type changes
      const selectedTagType = value ? tagTypes.find(tt => tt.id === value) : null;
      setFormData({
        ...formData,
        tagTypeId: value || null,
        tagTypeName: selectedTagType ? selectedTagType.name : null,
        // If tag type has a color palette, use the first color
        colourHex: selectedTagType && selectedTagType.colourPalette && selectedTagType.colourPalette.length > 0 ? selectedTagType.colourPalette[0] : formData.colourHex
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }
    // Clear error when field is edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  // Handle entity selection for site scope
  const handleEntityChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const entityId = e.target.value;
    setSelectedEntityId(entityId);
    setFilteredSites(sites.filter(site => site.entityId === entityId));
    setFormData({
      ...formData,
      scopeId: null,
      scopeName: null
    });
  };
  // Handle scope ID selection
  const handleScopeIdChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      value
    } = e.target;
    let scopeName = null;
    if (value) {
      switch (formData.scopeType) {
        case 'Entity':
          const entity = entities.find(e => e.id === value);
          scopeName = entity ? entity.name : null;
          break;
        case 'Site':
          const site = sites.find(s => s.id === value);
          scopeName = site ? site.name : null;
          break;
        case 'Vendor':
          const vendor = vendors.find(v => v.id === value);
          scopeName = vendor ? vendor.name : null;
          break;
      }
    }
    setFormData({
      ...formData,
      scopeId: value || null,
      scopeName
    });
  };
  // Handle color selection
  const handleColorChange = (color: string) => {
    setFormData({
      ...formData,
      colourHex: color
    });
  };
  // Validate form before submission
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Tag name is required';
    }
    if (formData.scopeType !== 'Application' && !formData.scopeId) {
      newErrors.scopeId = `${formData.scopeType} must be selected`;
    }
    // Validate date range
    if (formData.activeFrom && formData.activeTo) {
      const fromDate = new Date(formData.activeFrom);
      const toDate = new Date(formData.activeTo);
      if (fromDate > toDate) {
        newErrors.activeTo = 'Active To date must be after Active From date';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };
  // Get color palette from selected tag type
  const getTagTypeColorPalette = () => {
    if (formData.tagTypeId) {
      const selectedTagType = tagTypes.find(tt => tt.id === formData.tagTypeId);
      return selectedTagType && selectedTagType.colourPalette ? selectedTagType.colourPalette : presetColors;
    }
    return presetColors;
  };
  const colorPalette = getTagTypeColorPalette();
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-17704">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-17705">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-17706">
          {tag ? 'Edit Tag' : 'Create Tag'}
        </h3>
        <p className="mt-1 text-sm text-gray-500" data-id="element-17707">
          {tag ? 'Update the details of an existing tag' : 'Define a new tag for use across the system'}
        </p>
      </div>
      <form onSubmit={handleSubmit} data-id="element-17708">
        <div className="px-4 py-5 sm:p-6" data-id="element-17709">
          <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-17710">
            <div className="sm:col-span-3" data-id="element-17711">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-17712">
                Tag Name <span className="text-red-500" data-id="element-17713">*</span>
              </label>
              <div className="mt-1" data-id="element-17714">
                <input type="text" name="name" id="name" value={formData.name} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.name ? 'border-red-300' : ''}`} placeholder="e.g., Urgent, Project X, Department" data-id="element-17715" />
                {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-17716">{errors.name}</p>}
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-17717">
              <label htmlFor="tagTypeId" className="block text-sm font-medium text-gray-700" data-id="element-17718">
                Tag Type
              </label>
              <div className="mt-1" data-id="element-17719">
                <select id="tagTypeId" name="tagTypeId" value={formData.tagTypeId || ''} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17720">
                  <option value="" data-id="element-17721">No Tag Type</option>
                  {tagTypes.map(tagType => <option key={tagType.id} value={tagType.id} data-id="element-17722">
                      {tagType.name}
                    </option>)}
                </select>
              </div>
              <p className="mt-1 text-xs text-gray-500" data-id="element-17723">
                Grouping tags by type helps with organization
              </p>
            </div>
            <div className="sm:col-span-6" data-id="element-17724">
              <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-17725">
                Description
              </label>
              <div className="mt-1" data-id="element-17726">
                <textarea id="description" name="description" rows={3} value={formData.description} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Optional description of the tag" data-id="element-17727" />
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-17728">
              <label className="block text-sm font-medium text-gray-700" data-id="element-17729">
                Tag Color
              </label>
              <div className="mt-2 flex flex-wrap gap-2" data-id="element-17730">
                {colorPalette.map(color => <button key={color} type="button" className={`h-8 w-8 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${formData.colourHex === color ? 'ring-2 ring-offset-2 ring-indigo-500' : ''}`} style={{
                backgroundColor: color
              }} onClick={() => handleColorChange(color)} aria-label={`Color ${color}`} data-id="element-17731" />)}
                <div className="flex items-center" data-id="element-17732">
                  <label htmlFor="customColor" className="sr-only" data-id="element-17733">
                    Custom color
                  </label>
                  <input type="color" id="customColor" name="colourHex" value={formData.colourHex} onChange={handleInputChange} className="h-8 w-8 rounded-md border-gray-300" data-id="element-17734" />
                  <span className="ml-2 text-sm text-gray-500" data-id="element-17735">Custom</span>
                </div>
              </div>
              <div className="mt-3 flex items-center space-x-2" data-id="element-17736">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
                backgroundColor: `${formData.colourHex}20`,
                color: formData.colourHex,
                border: `1px solid ${formData.colourHex}`
              }} data-id="element-17737">
                  {formData.name || 'Tag Preview'}
                </span>
                <span className="text-sm text-gray-500" data-id="element-17738">Preview</span>
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-17739">
              <label htmlFor="scopeType" className="block text-sm font-medium text-gray-700" data-id="element-17740">
                Scope <span className="text-red-500" data-id="element-17741">*</span>
              </label>
              <div className="mt-1" data-id="element-17742">
                <select id="scopeType" name="scopeType" value={formData.scopeType} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17743">
                  <option value="Application" data-id="element-17744">Entire Application</option>
                  <option value="Entity" data-id="element-17745">Specific Entity</option>
                  <option value="Site" data-id="element-17746">Specific Site</option>
                  <option value="Vendor" data-id="element-17747">Specific Vendor</option>
                </select>
              </div>
            </div>
            {/* Conditional fields based on scope */}
            {formData.scopeType === 'Entity' && <div className="sm:col-span-3" data-id="element-17748">
                <label htmlFor="scopeId" className="block text-sm font-medium text-gray-700" data-id="element-17749">
                  Entity <span className="text-red-500" data-id="element-17750">*</span>
                </label>
                <div className="mt-1" data-id="element-17751">
                  <select id="scopeId" name="scopeId" value={formData.scopeId || ''} onChange={handleScopeIdChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.scopeId ? 'border-red-300' : ''}`} data-id="element-17752">
                    <option value="" data-id="element-17753">Select an entity</option>
                    {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-17754">
                        {entity.name}
                      </option>)}
                  </select>
                  {errors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-17755">
                      {errors.scopeId}
                    </p>}
                </div>
              </div>}
            {formData.scopeType === 'Site' && <>
                <div className="sm:col-span-3" data-id="element-17756">
                  <label htmlFor="entityId" className="block text-sm font-medium text-gray-700" data-id="element-17757">
                    Entity <span className="text-red-500" data-id="element-17758">*</span>
                  </label>
                  <div className="mt-1" data-id="element-17759">
                    <select id="entityId" name="entityId" value={selectedEntityId || ''} onChange={handleEntityChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17760">
                      <option value="" data-id="element-17761">Select an entity</option>
                      {entities.map(entity => <option key={entity.id} value={entity.id} data-id="element-17762">
                          {entity.name}
                        </option>)}
                    </select>
                  </div>
                </div>
                <div className="sm:col-span-3" data-id="element-17763">
                  <label htmlFor="scopeId" className="block text-sm font-medium text-gray-700" data-id="element-17764">
                    Site <span className="text-red-500" data-id="element-17765">*</span>
                  </label>
                  <div className="mt-1" data-id="element-17766">
                    <select id="scopeId" name="scopeId" value={formData.scopeId || ''} onChange={handleScopeIdChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.scopeId ? 'border-red-300' : ''}`} disabled={!selectedEntityId} data-id="element-17767">
                      <option value="" data-id="element-17768">Select a site</option>
                      {filteredSites.map(site => <option key={site.id} value={site.id} data-id="element-17769">
                          {site.name}
                        </option>)}
                    </select>
                    {!selectedEntityId && <p className="mt-1 text-xs text-gray-500" data-id="element-17770">
                        Please select an entity first
                      </p>}
                    {errors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-17771">
                        {errors.scopeId}
                      </p>}
                  </div>
                </div>
              </>}
            {formData.scopeType === 'Vendor' && <div className="sm:col-span-3" data-id="element-17772">
                <label htmlFor="scopeId" className="block text-sm font-medium text-gray-700" data-id="element-17773">
                  Vendor <span className="text-red-500" data-id="element-17774">*</span>
                </label>
                <div className="mt-1" data-id="element-17775">
                  <select id="scopeId" name="scopeId" value={formData.scopeId || ''} onChange={handleScopeIdChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.scopeId ? 'border-red-300' : ''}`} data-id="element-17776">
                    <option value="" data-id="element-17777">Select a vendor</option>
                    {vendors.map(vendor => <option key={vendor.id} value={vendor.id} data-id="element-17778">
                        {vendor.name}
                      </option>)}
                  </select>
                  {errors.scopeId && <p className="mt-1 text-sm text-red-600" data-id="element-17779">
                      {errors.scopeId}
                    </p>}
                </div>
              </div>}
            <div className="sm:col-span-3" data-id="element-17780">
              <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-17781">
                Active From
              </label>
              <div className="mt-1" data-id="element-17782">
                <input type="date" name="activeFrom" id="activeFrom" value={formData.activeFrom || ''} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" data-id="element-17783" />
              </div>
            </div>
            <div className="sm:col-span-3" data-id="element-17784">
              <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-17785">
                Active To
              </label>
              <div className="mt-1" data-id="element-17786">
                <input type="date" name="activeTo" id="activeTo" value={formData.activeTo || ''} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.activeTo ? 'border-red-300' : ''}`} data-id="element-17787" />
                {errors.activeTo && <p className="mt-1 text-sm text-red-600" data-id="element-17788">{errors.activeTo}</p>}
              </div>
            </div>
            <div className="sm:col-span-6" data-id="element-17789">
              <div className="flex items-start" data-id="element-17790">
                <div className="flex items-center h-5" data-id="element-17791">
                  <input id="isActive" name="isActive" type="checkbox" checked={formData.isActive} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-17792" />
                </div>
                <div className="ml-3 text-sm" data-id="element-17793">
                  <label htmlFor="isActive" className="font-medium text-gray-700" data-id="element-17794">
                    Active
                  </label>
                  <p className="text-gray-500" data-id="element-17795">
                    When checked, this tag will be available for use in the
                    system
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-17796">
          <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17797">
            <XIcon className="h-4 w-4 mr-1.5" data-id="element-17798" />
            Cancel
          </button>
          <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-17799">
            <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-17800" />
            {tag ? 'Update Tag' : 'Create Tag'}
          </button>
        </div>
      </form>
    </div>;
}